import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown, FlaskConical, Minus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Particles } from "@/components/catalog/Particles";
import { Spinner } from "@/components/catalog/TitleGrid";
import { useGuest } from "@/context/GuestContext";
import { useBilling } from "@/context/BillingContext";
import { api, formatApiError } from "@/lib/api";
import { useCreatorOn } from "@/lib/platform";

export const money = (n, cur = "usd") => new Intl.NumberFormat("en-US", { style: "currency", currency: cur.toUpperCase() }).format(n || 0);

const ROWS = [
  ["Shorts, trailers & episode 1 of every series", (l) => true],
  ["Full catalog incl. all AETRYX Originals", (l) => l.full_catalog],
  ["Profiles", (l) => `Up to ${l.max_profiles}`],
  ["Video quality", (l) => l.quality_label],
  ["Create Studio access", (l) => l.create_studio, true],
  ["AI credits / month", (l) => (l.ai_credits ? String(l.ai_credits) : false), true],
  ["Creator uploads & submissions", (l) => l.uploads, true],
  ["Priority review", (l) => l.priority_review, true],
  ["Export Submission Package", (l) => (l.priority_review ? "Coming Soon" : false), true],
  ["4K quality", (l) => l.quality_label === "4K"],
  ["Early access to Live Events", (l) => l.live_early_access],
];
const FAQ = [
  ["How does billing work?", "Premium and Premium+ are monthly subscriptions billed through Stripe. They renew automatically each month until you cancel. Prices exclude applicable sales tax / VAT, which Stripe calculates at checkout based on your billing address."],
  ["Can I cancel anytime?", "Yes. Cancel from Account → Billing and you keep full access until the end of the period you've paid for."],
  ["Can I switch between Premium and Premium+?", "Yes. Upgrades and downgrades apply instantly and Stripe prorates the difference."],
  ["What are AI credits?", "Credits power the AETRYX Create Studio AI tools. They refresh every billing period. The tools themselves arrive in the next release."],
  ["Does AETRYX publish to other streaming platforms?", "No. Studio's Export Submission Package (coming soon) helps you prepare materials for your own distribution submissions — AETRYX does not publish directly to third-party platforms."],
];

const Cell = ({ v }) => (v === true ? <Check className="mx-auto h-4 w-4 text-[#FFD86A]" /> : v ? <span className="text-white/85">{v}</span> : <Minus className="mx-auto h-4 w-4 text-white/20" />);

const PlanCard = ({ plan, current, busy, onPick }) => {
  const hi = Boolean(plan.badge);
  return (
    <div data-testid={`plan-card-${plan.code}`} className={`relative flex flex-col rounded-2xl border p-7 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 ${hi ? "border-[#FFD86A]/80 bg-gradient-to-b from-[#16130A] to-[#0B0B0D] shadow-[0_0_60px_rgba(212,175,55,0.22)] lg:scale-[1.04]" : "border-[#D4AF37]/20 bg-[#0B0B0D] hover:border-[#D4AF37]/50"}`}>
      {hi && <span data-testid={`plan-badge-${plan.code}`} className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-[#FFE9A3] to-[#D4AF37] px-4 py-1 text-[0.65rem] font-extrabold tracking-[0.25em] text-black">{plan.badge.toUpperCase()}</span>}
      <p className="eyebrow">{plan.name}</p>
      <p className="mt-1 text-sm text-[#8B8B8B] min-h-[40px]">{plan.tagline}</p>
      <p className="mt-4 flex items-baseline gap-1"><span data-testid={`plan-price-${plan.code}`} className="font-display text-5xl font-bold gold-text">{money(plan.price, plan.currency)}</span><span className="text-sm text-[#8B8B8B]">/{plan.interval}</span></p>
      {plan.price > 0 && <p data-testid={`plan-tax-note-${plan.code}`} className="mt-1 text-xs text-[#8B8B8B]">+ applicable taxes</p>}
      <ul className="mt-6 flex-1 space-y-2.5 text-sm">
        {plan.features.map((f) => <li key={f} className="flex gap-2 text-white/80"><Check className="mt-0.5 h-4 w-4 flex-none text-[#D4AF37]" />{f}</li>)}
      </ul>
      <button data-testid={`plan-cta-${plan.code}`} disabled={current || busy || plan.code === "free"} onClick={() => onPick(plan)}
        className={`mt-8 py-3 text-sm ${hi ? "btn-gold" : "btn-ghost-gold"} disabled:opacity-60 disabled:cursor-default`}>
        {current ? "Current plan" : plan.code === "free" ? "Included" : busy ? "Redirecting…" : `Choose ${plan.name}`}
      </button>
    </div>
  );
};

export default function Pricing() {
  const creatorOn = useCreatorOn();
  const navigate = useNavigate();
  const { billing, refresh } = useBilling();
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => setPlans([])); }, []);

  const currentCode = billing?.entitlements?.plan_code;
  const stripeLive = billing?.subscription?.live && billing.subscription.source === "stripe";

  const { isGuest, openAuth } = useGuest();
  const pick = async (plan) => {
    if (isGuest) return openAuth({ reason: "pricing", mode: "register" });
    setBusy(plan.code);
    try {
      if (stripeLive) {
        if (!window.confirm(`Switch to ${plan.name} for ${money(plan.price)}/month + applicable taxes? Stripe prorates the difference.`)) return;
        await api.post("/billing/change-plan", { plan_code: plan.code });
        await refresh();
        toast.success(`You're now on ${plan.name}`);
        navigate("/account#billing");
        return;
      }
      const { data } = await api.post("/billing/checkout", { plan_code: plan.code, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  if (!plans) return <div className="pt-32"><Spinner /></div>;
  return (
    <div data-testid="pricing-page" className="relative overflow-hidden pb-10">
      <section className="relative pt-32 md:pt-40 pb-14 text-center">
        <img src="/assets/art/bd_high_table.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/85 to-[#050505]" />
        <Particles count={18} />
        <div className="relative z-10 mx-auto max-w-3xl px-4">
          <p className="eyebrow flex items-center justify-center gap-2"><Crown className="h-4 w-4" /> Membership</p>
          <h1 className="mt-3 font-display font-bold text-4xl sm:text-5xl lg:text-6xl gold-text">Choose your universe</h1>
          <p className="mt-5 text-sm sm:text-base text-white/70">{creatorOn ? "Watch free, or unlock every AETRYX Original and the Create Studio." : "Watch free, or unlock every AETRYX Original in HD and 4K."}</p>
          <p data-testid="pricing-test-mode" className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#00F0FF]/30 px-3 py-1 text-[0.68rem] tracking-[0.2em] text-[#7CF7FF]"><FlaskConical className="h-3.5 w-3.5" /> TEST MODE · USE CARD 4242 4242 4242 4242</p>
        </div>
      </section>
      <section className="relative z-10 mx-auto grid max-w-6xl gap-6 px-4 sm:px-6 lg:grid-cols-3 lg:items-center">
        {plans.map((p) => <PlanCard key={p.code} plan={p} current={currentCode === p.code && (p.code === "free" || billing?.subscription?.live)} busy={busy === p.code} onPick={pick} />)}
      </section>
      <section className="mx-auto mt-20 max-w-5xl px-4 sm:px-6" data-testid="pricing-comparison">
        <h2 className="mb-6 font-display text-base md:text-lg uppercase tracking-[0.28em] gold-text flex items-center gap-2"><Sparkles className="h-4 w-4" /> Compare plans</h2>
        <div className="overflow-x-auto rounded-2xl border border-[#D4AF37]/20">
          <table className="w-full min-w-[560px] text-sm">
            <thead><tr className="bg-[#0B0B0D] text-left">
              <th className="p-4 font-semibold text-[#8B8B8B]">Feature</th>
              {plans.map((p) => <th key={p.code} className={`p-4 text-center font-display tracking-[0.12em] ${p.badge ? "text-[#FFD86A]" : "text-white"}`}>{p.name}</th>)}
            </tr></thead>
            <tbody>{ROWS.filter((r) => creatorOn || !r[2]).map(([label, fn]) => (
              <tr key={label} className="border-t border-[#D4AF37]/10">
                <td className="p-4 text-white/75">{label}</td>
                {plans.map((p) => <td key={p.code} className="p-4 text-center"><Cell v={fn(p.limits)} /></td>)}
              </tr>
            ))}</tbody>
          </table>
        </div>
      </section>
      <section className="mx-auto mt-16 max-w-3xl px-4 sm:px-6" data-testid="pricing-faq">
        <h2 className="mb-4 font-display text-base md:text-lg uppercase tracking-[0.28em] gold-text">FAQ</h2>
        <Accordion type="single" collapsible>
          {FAQ.filter(([q]) => creatorOn || !q.includes("AI credits")).map(([q, a], i) => (
            <AccordionItem key={q} value={`q${i}`} className="border-[#D4AF37]/15">
              <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left text-white hover:text-[#FFD86A] hover:no-underline">{q}</AccordionTrigger>
              <AccordionContent className="text-[#8B8B8B]">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
